import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PlaysTimeseries,
  CtaBar,
  PerGameBars,
  PlaysDonut,
  ConversionBar,
  HourlyActivity,
  PrizeDistribution,
  ConversionGauge,
} from "@/components/Charts";
import PerGameTable from "@/components/PerGameTable";
import api from "@/lib/api";
import {
  Activity,
  Gamepad2,
  Gift,
  TrendingUp,
  Users,
} from "lucide-react";

/**
 * Shared analytics dashboard for admin + merchant. All charts + KPIs in
 * one page. Pulls from /analytics/overview, /analytics/per-game,
 * /analytics/hourly and /analytics/prize-distribution (all role-scoped
 * server-side).
 */
export default function AnalyticsDashboard() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [perGame, setPerGame] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [prizes, setPrizes] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/analytics/per-game").then((r) => setPerGame(r.data.games || [])).catch(() => {});
    api.get("/analytics/hourly").then((r) => setHourly(r.data.hourly || [])).catch(() => {});
    api
      .get("/analytics/prize-distribution")
      .then((r) => setPrizes(r.data.prizes || []))
      .catch(() => {});
  }, []);

  if (!overview) {
    return <div className="text-muted-foreground">{t("dashboard.loading")}</div>;
  }

  const kpis = [
    {
      id: "plays",
      icon: Activity,
      label: t("dashboard.kpi_plays", "Total plays"),
      value: overview.total_plays,
      color: "from-violet-500/15 to-fuchsia-500/10 text-violet-700 dark:text-violet-300",
    },
    {
      id: "leads",
      icon: Users,
      label: t("dashboard.kpi_leads", "Unique leads"),
      value: overview.total_leads,
      color: "from-sky-500/15 to-indigo-500/10 text-sky-700 dark:text-sky-300",
    },
    {
      id: "wins",
      icon: Gift,
      label: t("dashboard.kpi_wins", "Wins"),
      value: overview.total_wins,
      color: "from-emerald-500/15 to-teal-500/10 text-emerald-700 dark:text-emerald-300",
    },
    {
      id: "games",
      icon: Gamepad2,
      label: t("dashboard.kpi_games", "Active games"),
      value: overview.active_games ?? overview.total_games ?? 0,
      color: "from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300",
    },
  ];

  const hasPerGame = perGame.length > 0;
  const hasPrizes = prizes.length > 0;
  const hasHourly = hourly.some((h) => h.plays > 0);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ id, icon: Icon, label, value, color }) => (
          <div
            key={id}
            data-testid={`kpi-${id}`}
            className={`qrise-card p-5 relative overflow-hidden bg-gradient-to-br ${color}`}
          >
            <div className="flex items-start justify-between">
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-bold opacity-80">
                {label}
              </div>
              <Icon className="h-4 w-4 opacity-60" />
            </div>
            <div className="mt-3 text-3xl font-semibold tabular-nums">
              {value ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Gauge + timeseries */}
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="qrise-card p-6 flex flex-col">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            {t("dashboard.conversion_rate", "Conversion rate")}
          </div>
          <div className="flex-1 flex items-center justify-center">
            <ConversionGauge value={overview.conversion_rate || 0} />
          </div>
          <div className="text-[11px] text-muted-foreground text-center mt-2">
            {t("dashboard.wins_over_plays", "wins / plays (all-time)")}
          </div>
        </div>
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
            {t("dashboard.timeseries")}
          </div>
          <PlaysTimeseries data={overview.timeseries} />
        </div>
      </div>

      {/* Per-game bars + Donut */}
      {hasPerGame && (
        <div className="grid lg:grid-cols-[1fr_420px] gap-4">
          <div className="qrise-card p-6">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
              {t("dashboard.chart_per_game_bars", "Plays · leads · wins per game")}
            </div>
            <PerGameBars data={perGame} />
          </div>
          <div className="qrise-card p-6">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
              {t("dashboard.chart_plays_donut", "Play distribution")}
            </div>
            <PlaysDonut data={perGame} />
          </div>
        </div>
      )}

      {/* Conversion ranking + CTA performance */}
      <div className="grid lg:grid-cols-2 gap-4">
        {hasPerGame && (
          <div className="qrise-card p-6">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
              {t("dashboard.chart_conversion_per_game", "Conversion rate by game")}
            </div>
            <ConversionBar data={perGame} />
          </div>
        )}
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
            {t("dashboard.cta_performance")}
          </div>
          {overview.cta_stats?.length ? (
            <CtaBar data={overview.cta_stats} />
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("dashboard.no_data")}
            </div>
          )}
        </div>
      </div>

      {/* Hourly + Prizes */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
            {t("dashboard.chart_hourly", "Activity by hour (last 30 days)")}
          </div>
          {hasHourly ? (
            <HourlyActivity data={hourly} />
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("dashboard.no_data")}
            </div>
          )}
        </div>
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
            {t("dashboard.chart_prizes", "Most won prizes")}
          </div>
          {hasPrizes ? (
            <PrizeDistribution data={prizes} />
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("dashboard.no_prizes_yet", "No prize won yet")}
            </div>
          )}
        </div>
      </div>

      {/* Full per-game table */}
      <div className="qrise-card p-6">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
          {t("dashboard.per_game")}
        </div>
        <PerGameTable games={perGame} />
      </div>
    </div>
  );
}
